import { createContext, useCallback, useContext, useMemo, useSyncExternalStore, type ComponentType, type ReactNode } from 'react';
import { Modal, type ModalProps } from '../overlays/Modal/Modal.js';
import { Dialog } from '../overlays/Dialog/Dialog.js';
import { modalsStore, type ModalItem, type OpenConfirmModalPayload } from './modals-store.js';
import { openModal, openConfirmModal, openContextModal, closeModal, closeAllModals } from './modals.js';

/**
 * `<ModalsProvider>`: mount once at the React root to enable the global
 * imperative API:
 *
 *   import { openModal, openConfirmModal, openContextModal } from '@repletoryreach/sdk-ui/modals';
 *
 *   openModal({ title: 'Edit profile', children: <Form /> });
 *
 *   openConfirmModal({
 *     title: 'Delete contact?',
 *     children: <Text>This cannot be undone.</Text>,
 *     labels: { confirm: 'Delete', cancel: 'Keep' },
 *     confirmProps: { color: 'red' },
 *     onConfirm: () => api.delete(id)
 *   });
 *
 *   openContextModal({ modal: 'editTemplate', innerProps: { templateId } });
 *
 * For typed registered modals (`openContextModal`), pass a `modals` map:
 *
 *   <ModalsProvider modals={{ editTemplate: EditTemplateModal }}>
 *     <App />
 *   </ModalsProvider>
 */

export interface ContextModalProps<TInner = unknown> {
  id: string;
  context: { closeModal: () => void; closeAll: () => void };
  innerProps: TInner;
}

export interface ModalsProviderProps {
  children: ReactNode;
  /** Map of named modal components used by `openContextModal({ modal })`. */
  modals?: Record<string, ComponentType<ContextModalProps<never>>>;
  /** Overlay props applied to every modal in the stack (default blur stays). */
  defaultModalProps?: Partial<ModalProps>;
}

interface ModalsContextValue {
  openModal: typeof openModal;
  openConfirmModal: typeof openConfirmModal;
  openContextModal: typeof openContextModal;
  closeModal: typeof closeModal;
  closeAllModals: typeof closeAllModals;
}

const ModalsContext = createContext<ModalsContextValue | null>(null);

/** Hook for components that prefer context access over the singleton imports. */
export function useModals(): ModalsContextValue {
  const ctx = useContext(ModalsContext);
  if (!ctx) {
    // Fall through to imperative API: components still work outside provider
    return { openModal, openConfirmModal, openContextModal, closeModal, closeAllModals };
  }
  return ctx;
}

export function ModalsProvider({ children, modals = {}, defaultModalProps }: ModalsProviderProps): ReactNode {
  const items = useSyncExternalStore(
    modalsStore.subscribe,
    modalsStore.getState,
    () => [] as ReturnType<typeof modalsStore.getState>
  );

  const ctxValue = useMemo<ModalsContextValue>(
    () => ({ openModal, openConfirmModal, openContextModal, closeModal, closeAllModals }),
    []
  );

  const handleClose = useCallback((id: string, onClose?: () => void) => {
    modalsStore.close(id);
    onClose?.();
    // Evict shortly after to allow the exit animation to play.
    setTimeout(() => modalsStore.remove(id), 250);
  }, []);

  return (
    <ModalsContext.Provider value={ctxValue}>
      {children}
      {items.map((item) => renderItem(item, modals, defaultModalProps, handleClose))}
    </ModalsContext.Provider>
  );
}

function renderItem(
  item: { id: string; opened: boolean; props: ModalItem },
  modals: Record<string, ComponentType<ContextModalProps<never>>>,
  defaults: Partial<ModalProps> | undefined,
  handleClose: (id: string, onClose?: () => void) => void
): ReactNode {
  const { id, opened, props } = item;

  if (props.kind === 'modal') {
    const { children, onClose, ...rest } = props.payload;
    return (
      <Modal
        key={id}
        opened={opened}
        onClose={() => handleClose(id, onClose)}
        {...defaults}
        {...rest}
      >
        {children}
      </Modal>
    );
  }

  if (props.kind === 'confirm') {
    const payload = props.payload as OpenConfirmModalPayload;
    const {
      children, onConfirm, onCancel, labels, confirmProps, cancelProps,
      closeOnConfirm = true, closeOnCancel = true, ...rest
    } = payload;
    return (
      <Dialog
        key={id}
        opened={opened}
        onClose={() => handleClose(id, onCancel)}
        labels={labels}
        confirmProps={confirmProps}
        cancelProps={cancelProps}
        onConfirm={() => { onConfirm?.(); if (closeOnConfirm) handleClose(id); }}
        onCancel={() => { onCancel?.(); if (closeOnCancel) handleClose(id); }}
        {...defaults}
        {...rest}
      >
        {children}
      </Dialog>
    );
  }

  // context modal
  const { modal, innerProps, onClose, ...rest } = props.payload;
  const Component = modals[modal];
  if (!Component) {
    if (typeof console !== 'undefined') {
      console.warn(`[sdk-ui/modals] openContextModal: no component registered for "${modal}"`);
    }
    return null;
  }
  return (
    <Modal
      key={id}
      opened={opened}
      onClose={() => handleClose(id, onClose)}
      {...defaults}
      {...rest}
    >
      <Component
        id={id}
        context={{
          closeModal: () => handleClose(id, onClose),
          closeAll: () => closeAllModals()
        }}
        innerProps={innerProps as never}
      />
    </Modal>
  );
}
